/**
 * <ThreeDotMenu> — a generic overflow menu (molecule, L0.1).
 *
 * The maison replacement for the legacy `Menu3Point`: an icon-only `<IconButton>` (ellipsis)
 * that opens an anchored popover of tappable rows. Each row is a `<LabeledIconButton>` (icon +
 * text, `ghost` variant) — the menu never hand-rolls an icon+label row.
 *
 * Fully generic: it takes `items` ({ icon, text, onPress }) — no domain coupling. The popover is
 * a plain RN `<Modal>` (NOT react-native-paper), anchored under the trigger via `measureInWindow`.
 *
 * `trigger` (optionnel) : un autre déclencheur que les trois points — une chip qui porte ses options (« Quiz
 * d'abord » au centre du live : ses options s'ouvrent depuis elle, au lieu d'un bouton à côté). Le menu se cale
 * alors sous son bord gauche. Absent : les trois points, le menu calé à droite, comme avant.
 */
import React, { useRef, useState } from "react"
import { Dimensions, Modal, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { LabeledIconButton } from "../../atoms/Buttons/LabeledIconButton"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { type IconName } from "../../atoms/icons/Icon"
import { shadowStyle } from "../../utils/shadow"

/** One menu row: a leading glyph, a label, and its action. */
type MenuItem = {
    icon: IconName
    text: string
    onPress: () => void
}

export type ThreeDotMenuProps = {
    items: MenuItem[]
    /** Le déclencheur, à la place des trois points : `onPress` ouvre le menu, `open` dit qu'il l'est. */
    trigger?: (state: { onPress: () => void; open: boolean }) => React.ReactNode
    /** Trigger footprint in px — defaults to 32. */
    size?: number
    testID?: string
    accessibilityLabel?: string
}

const MENU_MIN_WIDTH = 200

/** Where the popover opens, in window coordinates: under the trigger's right edge, or its left one. */
type Anchor = { top: number; right?: number; left?: number }

export function ThreeDotMenu(props: ThreeDotMenuProps) {
    const { items, trigger, size = 32, testID, accessibilityLabel = "Menu" } = props

    // Measurement ref on a positioning wrapper (not a layout flex container).
    const triggerRef = useRef<View>(null)
    const [anchor, setAnchor] = useState<Anchor | null>(null)

    const open = () => {
        triggerRef.current?.measureInWindow((x, y, w, h) => {
            const windowWidth = Dimensions.get("window").width
            setAnchor(
                trigger != null
                    ? { top: y + h + 4, left: Math.max(Math.min(x, windowWidth - MENU_MIN_WIDTH - 8), 8) }
                    : { top: y + h + 4, right: Math.max(windowWidth - (x + w), 8) }
            )
        })
    }

    const close = () => setAnchor(null)

    return (
        <View ref={triggerRef} collapsable={false}>
            {trigger != null ? (
                trigger({ onPress: open, open: anchor != null })
            ) : (
                <IconButton
                    iconName="ellipsis-h"
                    appearance="transparent"
                    iconColor="text-muted"
                    size={size}
                    onPress={open}
                    testID={testID}
                    accessibilityLabel={accessibilityLabel}
                />
            )}
            <Modal visible={anchor != null} transparent animationType="fade" onRequestClose={close}>
                <Pressable style={styles.backdrop} onPress={close} />
                {anchor != null && (
                    <View style={[styles.card, { top: anchor.top, right: anchor.right, left: anchor.left }]}>
                        <Stack gap="0">
                            {items.map((item, index) => (
                                <LabeledIconButton
                                    key={`${item.text}-${index}`}
                                    iconName={item.icon}
                                    label={item.text}
                                    variant="ghost"
                                    size="sm"
                                    fullWidth
                                    onPress={() => {
                                        close()
                                        item.onPress()
                                    }}
                                />
                            ))}
                        </Stack>
                    </View>
                )}
            </Modal>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    backdrop: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    card: {
        position: "absolute" as const,
        minWidth: MENU_MIN_WIDTH,
        backgroundColor: theme.colors["surface-raised"],
        borderRadius: theme.radius.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
        paddingVertical: theme.spacing["1"],
        paddingHorizontal: theme.spacing["1"],
        ...shadowStyle(theme, "menu"),
    },
}))
